import { useEffect, useState, useCallback } from 'react'

import { syncStatusBar } from '@/platform'

type Theme = 'light' | 'dark'

const THEME_STORAGE_KEY = 'theme'
const TRANSITION_CLASS = 'theme-transition'
const TRANSITION_DURATION = 300

export function useTheme() {
  const [theme, setThemeState] = useState<Theme>(() => {
    if (typeof window === 'undefined') return 'light'

    const stored = localStorage.getItem(THEME_STORAGE_KEY) as Theme | null
    if (stored) return stored

    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'
  })

  // Apply theme to DOM and persist to localStorage
  const applyTheme = useCallback((newTheme: Theme, withTransition = false) => {
    const root = document.documentElement

    // Add transition class for smooth animation
    if (withTransition) {
      root.classList.add(TRANSITION_CLASS)
    }

    if (newTheme === 'dark') {
      root.classList.add('dark')
    } else {
      root.classList.remove('dark')
    }

    localStorage.setItem(THEME_STORAGE_KEY, newTheme)
    void syncStatusBar(newTheme)

    // Remove transition class after animation completes
    if (withTransition) {
      setTimeout(() => {
        root.classList.remove(TRANSITION_CLASS)
      }, TRANSITION_DURATION)
    }
  }, [])

  // Initial theme application (no transition on mount)
  useEffect(() => {
    applyTheme(theme, false)
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  // Listen for system theme changes
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)')

    const handleChange = (e: MediaQueryListEvent) => {
      // Only update if user hasn't set a preference
      const stored = localStorage.getItem(THEME_STORAGE_KEY)
      if (!stored) {
        const newTheme = e.matches ? 'dark' : 'light'
        setThemeState(newTheme)
        applyTheme(newTheme, true)
      }
    }

    mediaQuery.addEventListener('change', handleChange)
    return () => mediaQuery.removeEventListener('change', handleChange)
  }, [applyTheme])

  const setTheme = useCallback(
    (newTheme: Theme) => {
      setThemeState(newTheme)
      applyTheme(newTheme, true)
    },
    [applyTheme]
  )

  const toggleTheme = useCallback(() => {
    const newTheme = theme === 'light' ? 'dark' : 'light'
    setTheme(newTheme)
  }, [theme, setTheme])

  const setLightTheme = useCallback(() => setTheme('light'), [setTheme])
  const setDarkTheme = useCallback(() => setTheme('dark'), [setTheme])

  return {
    theme,
    setTheme,
    toggleTheme,
    setLightTheme,
    setDarkTheme,
    isDark: theme === 'dark',
    isLight: theme === 'light',
  }
}

export default useTheme
